import { SlippageTolerance } from 'src/@types/types';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useCallback } from 'react';

interface Params {
	updateHandler: ((newTolerance: SlippageTolerance) => void) | undefined;
	hideDynamic: boolean | undefined;
	updateStateFromCustomInput: (value: string) => void;
	updateStateFromPreset: (value: string) => void;
}

export default function useSlippageToleranceInputHandlers({
	updateHandler,
	hideDynamic,
	updateStateFromCustomInput,
	updateStateFromPreset,
}: Params) {
	const setDriftStore = useDriftStore((s) => s.set);

	// Either invokes the passed callback or updates the trade form store.
	const updateSlippageTolerance = useCallback(
		(newTolerance: SlippageTolerance) => {
			if (updateHandler) {
				updateHandler(newTolerance);
				return;
			}

			setDriftStore((s) => {
				s.tradeForm.slippageTolerance = newTolerance;
			});
		},
		[setDriftStore, updateHandler]
	);

	// Performs some extra validation on the incoming value before updating.
	const handleCustomInputChange = useCallback(
		(value: string) => {
			const parsedVal = parseFloat(value);

			let slippageToStore: SlippageTolerance = parsedVal;
			let customInputValueToSet = value;
			let shouldUpdateStore = true;

			// If the value is NaN or < 0 we set the slippage to dynamic but we don't
			// force-replace the user's input value with anything.
			if (isNaN(parsedVal) || parsedVal < 0) {
				slippageToStore = 'dynamic';
				shouldUpdateStore = !hideDynamic;
			}

			// If the value is > 100 we both set the store value to 100 and force-replace
			// the user's input value with 100.
			if (parsedVal > 100) {
				slippageToStore = 100;
				customInputValueToSet = '100';
			}

			updateStateFromCustomInput(customInputValueToSet);

			if (shouldUpdateStore) {
				updateSlippageTolerance(slippageToStore);
			}
		},
		[updateSlippageTolerance, hideDynamic, updateStateFromCustomInput]
	);

	const handlePresetClick = useCallback(
		(value: SlippageTolerance) => {
			updateStateFromPreset(value.toString());
			updateSlippageTolerance(value);
		},
		[updateSlippageTolerance, updateStateFromPreset]
	);

	return {
		handleCustomInputChange,
		handlePresetClick,
	};
}
